import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid2, Paper } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IRowNode,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { useQueryClient } from "@tanstack/react-query";
import { useTableData } from "../../hooks/hooks";
import { useDelaysOptionFunctions } from "../../hooks/useDelaysOptionFunctions";
import { DelayByShift } from "../../models/DelayByShift";
import { deleteDelay } from "../../repositories/DelaysManagerRepository";
import { NewSelectAdjustmentModal } from "../adjustment/modal/NewSelectAdjustmentModal";
import { SplitDelaysModal } from "../modals/SplitDelaysModal";
import { UpsertJustifyDelayModal } from "../modals/UpsertJustifyDelayModal";

type Props = {
	delayId: string | null;
	action: "edit" | "justify" | "split" | null;
	isLoading: boolean;
	rows: DelayByShift[];
	refreshData: Function;
	assetId?: number;
	updateUrl: (
		newIncludeMicroDelays?: boolean,
		newOnlyUndeclared?: boolean,
		newAction?: string | null,
		newDelayId?: string | null,
	) => void;
};

export const DelaysManagerTable = ({
	delayId,
	action,
	isLoading,
	rows,
	refreshData,
	assetId,
	updateUrl,
}: Props) => {
	const queryClient = useQueryClient();

	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [selectedData, setSelectedData] = useState<DelayByShift | null>(null);
	const [showModal, setShowModal] = useState<
		"new" | "edit" | "delete" | "justify" | "split" | ""
	>("");
	const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const {
		createData,
		justifyData,
		splitData,
		adjustment,
		getMenuOptions,
		messageDeleteModal,
		setMessageDeleteModal,
	} = useDelaysOptionFunctions({
		setSelectedData,
		setShowModal,
		setShowAdjustmentModal,
		updateUrl,
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs } = useTableData({
		justifyData,
		splitData,
		adjustment,
		showContextMenu,
	});
	const { defaultColDef } = useGridDefinitions({});

	const handleDeleteDelay = async (value?: string) => {
		setShowModal("");
		setMessageDeleteModal("");
		updateUrl(undefined, undefined, null, null);
		if (value !== "OK") return;
		if (selectedData === null) return;
		const resp = await deleteDelay(selectedData.DelayId);
		if (resp.ok) {
			setNotificationMessage("Delay deleted successfully");
			setSelectedData(null);
			await queryClient.invalidateQueries(["delays"]);
		} else {
			setError(resp.message);
		}
	};

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		showContextMenu(event as any, data, "delayManagerContext");
		if (data) {
			params.api.deselectAll();
			params.node?.setSelected(true);
		}
		return [];
	};

	const onModalHide = async (update: boolean) => {
		if (update) {
			await queryClient.invalidateQueries(["delays"]);
		}
		setShowModal("");
		setSelectedData(null);
		updateUrl(undefined, undefined, null, null);
		if (gridApi) {
			gridApi.deselectAll();
		}
	};

	useEffect(() => {
		setShowModal(action ?? "");
	}, [action]);

	useEffect(() => {
		if (!isLoading && gridApi) {
			if (delayId) {
				let foundNode = null;
				gridApi.forEachNode((node) => {
					if (node.data?.DelayId?.toString() === delayId) {
						foundNode = node;
					}
				});

				if (foundNode) {
					gridApi.ensureNodeVisible(foundNode);
					(foundNode as IRowNode).setSelected(true);
					setSelectedData((foundNode as IRowNode).data);
				}
			} else {
				setSelectedData(null);
			}
		}
	}, [isLoading, gridApi, delayId]);

	useEffect(() => {
		registerConfig({
			id: "delayManagerContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<div onContextMenu={(e) => e.preventDefault()}>
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				sx={{
					height: "72vh",
					minHeight: "72vh",
				}}
				spacing={1}
				component={Paper}
				elevation={2}
			>
				<Grid2 size={{ md: 12, xs: 12 }} height={"100%"}>
					<AgGridReact<DelayByShift>
						loading={isLoading}
						onGridReady={(params) => setGridApi(params.api)}
						pagination={false}
						animateRows
						rowHeight={38}
						headerHeight={35}
						singleClickEdit
						tooltipShowDelay={0}
						tooltipHideDelay={2000}
						rowData={rows}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getContextMenuItems={getContextMenuItems}
						onRowClicked={(e) => {
							updateUrl(
								undefined,
								undefined,
								action ?? null,
								e.data?.DelayId?.toString(),
							);
						}}
						onCellDoubleClicked={(e) => {
							updateUrl(
								undefined,
								undefined,
								"justify",
								e.data?.DelayId?.toString(),
							);
							justifyData(e.data!);
						}}
						getRowId={(params) => `${params.data.DelayId}`}
						loadingOverlayComponent={CenteredLazyLoading}
						gridOptions={{
							rowSelection: {
								mode: "singleRow",
								checkboxes: false,
								enableClickSelection: true,
							},
						}}
						onCellKeyDown={(event) => {
							if ((event.event as KeyboardEvent).key === "Escape") {
								event.api.hidePopupMenu();
							}
						}}
					/>
				</Grid2>
				<Grid2 container size={{ md: 12 }} justifyContent="flex-end">
					<Grid2 size={{ md: 2, xs: 12 }}>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							onClick={() => {
								createData();
							}}
						>
							NEW DELAY
						</Button>
					</Grid2>
				</Grid2>
			</Grid2>
			<ConfirmationDialogRaw
				title="Confirm Delete"
				message={messageDeleteModal}
				open={showModal === "delete"}
				onClose={handleDeleteDelay}
				suffixTitle={"Delays Manager"}
			/>
			<UpsertJustifyDelayModal
				showModal={showModal}
				onHide={onModalHide}
				delayId={selectedData?.DelayId ?? null}
				assetId={assetId}
				suffixTitle="Delays Manager"
			/>
			<SplitDelaysModal
				show={showModal === "split"}
				onHide={onModalHide}
				delayId={selectedData?.DelayId ?? null}
				assetId={assetId}
			/>
			<NewSelectAdjustmentModal
				showModal={showAdjustmentModal ? "new" : ""}
				onHide={async (update: boolean) => {
					await onModalHide(update);
					setShowAdjustmentModal(false);
				}}
				millAdjustmentId={selectedData?.MillAdjustmentId ?? null}
				delay={{
					delayId: selectedData?.DelayId!,
					delayEnd: selectedData?.End!,
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</div>
	);
};
